import { Component, inject, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import { ServiceInstructionDocument } from '../../../../models/api/view/claim/documentation/service-instructions/service-instruction-document';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../../base/base.component';
import { LinkLabelComponent } from '../../../core/labels/link-label.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ProgressbarComponent } from '../../../core/labels/progressbar.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-service-instruction',
  imports: [
    PageHeaderComponent,
    TableComponent,
    ColumnComponent,
    LinkLabelComponent,
    EmptyStateComponent,
    ProgressbarComponent,
  ],
  template: `
    <app-page-header label="Service Instructions" />
    <div class="tw-flex tw-pb-8">
      <div class="tw-w-full">
        @if (loading) {
          <app-empty-state
            height="300px"
            icon="fa-light fa-file-lines"
            label="Loading service instructions"
            message="Retrieving documents..."
          >
            <div class="tw-py-4">
              <app-progressbar />
            </div>
          </app-empty-state>
        } @else if (documents.length === 0) {
          <app-empty-state
            height="300px"
            icon="fa-light fa-file-lines"
            label="No service instructions"
            message="There are no service instruction documents available for this claim."
          />
        } @else {
          <app-table
            [dataSource]="documents"
            [showPaginator]="true"
            [pageSize]="5"
            [showSort]="false"
            [showFilter]="false"
            [showFilterButton]="false"
            [showRecordLabel]="false"
            sortField="updateDate"
            [sortOrder]="1"
          >
            <app-column label="Title" name="title" width="30%">
              <ng-template #download let-data>
                <app-link-label (click)="onViewDocument(data, $event)">
                  {{ data.title }}
                </app-link-label>
              </ng-template>
            </app-column>
            <app-column
              name="updateDate"
              label="Update Date"
              format="d"
              width="70"
            />
          </app-table>
        }
      </div>
    </div>
  `,
  styles: [],
})
export class ServiceInstructionComponent
  extends BaseComponent
  implements OnInit
{
  private snackbar = inject(Snackbar);
  private messageService = inject(MessageService);

  documents: ServiceInstructionDocument[] = [];
  loading = true;

  async ngOnInit(): Promise<void> {
    this.view.showLoading();
    await this.loadDocuments();
    this.view.hideLoading();
  }

  private async loadDocuments(): Promise<void> {
    const claim = this.view.currentClaim();
    if (!claim) {
      this.loading = false;
      return;
    }

    const claimKey: ClaimKey = {
      recordSource: claim.recordSource,
      recordId: claim.recordId,
    };

    const response = await this.api.serviceInstructions.get(claimKey);
    this.documents = response.documents || [];
    this.loading = false;
  }

  async onViewDocument(
    doc: ServiceInstructionDocument,
    event?: Event,
  ): Promise<void> {
    event?.stopPropagation();

    this.snackbar.show('Downloading...', 'Info');

    try {
      const blob = await this.api.serviceInstructions.downloadDocument(
        doc.uniqueId,
      );

      // Open PDF in new tab
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch {
      this.messageService.clear('snackbar');
      this.snackbar.show('Failed to open document', 'Error');
    }
  }
}
